/*
 * @Description: 实时定位驾驶舱 — 地图组件
 *   高德地图 + 成员气泡（在线绿/离线灰/选中蓝）+ 自适应视野。
 *   模式参照 src/pages/memberLocation/components/MapContainer（独立实例，不共用全局 MapInstance）。
 *   一期暂用普通单点 marker（聚合 MarkerCluster 待后续期验证后再接入）。
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import AMapLoader from '@amap/amap-jsapi-loader'
import CONFIG from '../../../../constants/config'
import {
  CockpitMapStyle,
  MAP_FEATURES,
  SELECTED_TRACK_COLOR,
  TRACK_COLORS
} from '../../constants'
import { applyMapStyle } from '../../utils/mapStyle'
import {
  MemberDetail,
  MemberLocationItem,
  parseLngLat,
  TrackStatsData
} from '../../types/cockpit.types'
import { PoiItem } from '../../types/poi.types'
import { TrackSegment } from '../../utils/trackSegments'
import './index.scss'

/** 单成员的轨迹分段（供折线渲染） */
export interface MemberTrajectory {
  userId: string
  segments: TrackSegment[]
}

/** 地图组件暴露给父组件的方法 */
export interface CockpitMapRef {
  /**
   * 居中到指定坐标并放大到默认点击缩放（16）。
   * keepCloserZoom=true 时：若当前缩放比默认更大（视野更近），只平移不缩放（不把用户的近视野缩小）。
   */
  centerTo: (lng: number, lat: number, keepCloserZoom?: boolean) => void
  /** 自适应显示全部成员 */
  fitViewToAll: () => void
  /** 容器从隐藏恢复显示后重排地图（display:none 时容器尺寸为 0，否则灰屏/错位）；不改视野 */
  resize: () => void
}

interface CockpitMapProps {
  /** 全员最新位置（或快照位置） */
  members: MemberLocationItem[]
  /** 当前选中的成员 userId（null = 未选中） */
  selectedUserID: string | null
  /** 各成员轨迹分段（蓝实线 + 中断红虚线） */
  trajectories: MemberTrajectory[]
  /** 选中成员的详情（A4）；非空时在气泡上方弹详情卡 */
  detail: MemberDetail | null
  /** 点击成员气泡回调；点击地图空白处回调 null */
  onSelectMember: (member: MemberLocationItem | null) => void
  /** 详情卡「查看完整轨迹」 */
  onViewTrack: (userId: string) => void
  /** 关闭详情卡（X / 点空白） */
  onCloseDetail: () => void
  /** 自适应视野的触发 key（如日期）：变化后等新数据绘制完，把轨迹+成员整体居中一次 */
  fitKey: string
  /** 已保存的点位（E1），以图标 marker 常驻展示在地图上 */
  pois: PoiItem[]
  /** 选中成员的 B4 轨迹统计（详情卡 4 数据格）；null=未取到 */
  detailStats: TrackStatsData | null
  /** 详情卡地址下方小字（在线=移动中/停留中，离线/历史=最后位置） */
  detailStateLabel: string
  /** 地图样式（纯净 / 普通 / 卫星） */
  mapStyle: CockpitMapStyle
}

/** 生成单个成员气泡（头像 pin）的 HTML 内容 */
function buildMemberMarkerHtml(
  member: MemberLocationItem,
  selected: boolean
): string {
  const statusClass = member.online ? 'is-online' : 'is-offline'
  const selectedClass = selected ? 'is-selected' : ''
  // 无头像时展示姓名后两位（如 徐兴泽 → 兴泽）
  const shortName = (member.name || '?').slice(-2)
  const avatarInner = member.avatar
    ? `<img src="${member.avatar}" alt="" />`
    : `<span class="cockpit-marker__name">${shortName}</span>`

  return `<div class="cockpit-marker ${statusClass} ${selectedClass}">
      <div class="cockpit-marker__body">
        <div class="cockpit-marker__avatar">${avatarInner}</div>
        <span class="cockpit-marker__dot"></span>
      </div>
      <span class="cockpit-marker__nick">${member.name || ''}</span>
      <span class="cockpit-marker__shadow"></span>
    </div>`
}

/**
 * 信号格数 → 文案（设计图状态行展示「良好/一般/较弱」而非格数）。
 */
function signalLabel(signal: number): string {
  if (signal >= 4) return '良好'
  if (signal >= 2) return '一般'
  return '较弱'
}

const SVG_NS = 'http://www.w3.org/2000/svg'

/** 创建 SVG 子元素（不用 innerHTML，避免 XSS 隐患） */
function svgEl(
  tag: string,
  attrs: Record<string, string | number>
): SVGElement {
  const el = document.createElementNS(SVG_NS, tag) as SVGElement
  Object.keys(attrs).forEach((k) => el.setAttribute(k, String(attrs[k])))
  return el
}

/** 电量图标（填充宽度按百分比；≤20% 红，否则绿） */
function batteryIcon(battery: number): SVGElement {
  const lvl = Math.max(0, Math.min(100, battery || 0))
  const svg = svgEl('svg', {
    width: 20,
    height: 11,
    viewBox: '0 0 20 11',
    fill: 'none'
  })
  svg.appendChild(
    svgEl('rect', {
      x: 0.5,
      y: 0.5,
      width: 15,
      height: 10,
      rx: 2.5,
      stroke: '#bfbfbf'
    })
  )
  svg.appendChild(
    svgEl('rect', {
      x: 2.5,
      y: 2.5,
      width: (10 * lvl) / 100,
      height: 6,
      rx: 1,
      fill: lvl <= 20 ? '#ff4d4f' : '#52c41a'
    })
  )
  svg.appendChild(
    svgEl('rect', {
      x: 16.5,
      y: 3.5,
      width: 2.5,
      height: 4,
      rx: 1,
      fill: '#bfbfbf'
    })
  )
  return svg
}

/** 信号图标（4 格，按 signal 高亮） */
function signalIcon(signal: number): SVGElement {
  const svg = svgEl('svg', { width: 15, height: 9, viewBox: '0 0 15 9' })
  ;[3, 5, 7, 9].forEach((h, i) => {
    svg.appendChild(
      svgEl('rect', {
        x: i * 4,
        y: 9 - h,
        width: 3,
        height: h,
        rx: 0.5,
        fill: (signal || 0) >= i + 1 ? '#595959' : '#d9d9d9'
      })
    )
  })
  return svg
}

/** 定位 pin 图标 */
function pinIcon(): SVGElement {
  const svg = svgEl('svg', {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: '#1890ff'
  })
  svg.appendChild(
    svgEl('path', {
      d: 'M12 2C7.6 2 4 5.6 4 10c0 5.4 7 11.5 7.3 11.7.4.4 1 .4 1.4 0C13 21.5 20 15.4 20 10c0-4.4-3.6-8-8-8zm0 11a3 3 0 110-6 3 3 0 010 6z'
    })
  )
  return svg
}

/**
 * 用 DOM 构建成员详情卡（InfoWindow 自定义内容，对齐设计图）。
 * 姓名/地址经 textContent 写入避免 XSS；图标用 createElementNS（不走 innerHTML）。
 * 关闭/查看轨迹按钮带 data-action，事件由调用方委托处理。
 *
 * @param d - 成员详情（A4）
 * @param stats - B4 轨迹统计（4 数据格）；null 时数值显示 '--'
 * @param stateLabel - 地址下方小字（在线=移动中/停留中，离线/历史=最后位置）
 */
function buildDetailCard(
  d: MemberDetail,
  stats: TrackStatsData | null,
  stateLabel: string
): HTMLDivElement {
  const card = document.createElement('div')
  card.className = 'cockpit-detail-card'

  // 头部：姓名 + 关闭
  const header = document.createElement('div')
  header.className = 'cockpit-detail-card__header'
  const title = document.createElement('span')
  title.className = 'cockpit-detail-card__title'
  title.textContent = d.name || '成员'
  const close = document.createElement('span')
  close.className = 'cockpit-detail-card__close'
  close.textContent = '×'
  close.setAttribute('data-action', 'close')
  header.appendChild(title)
  header.appendChild(close)

  // 状态行：状态点·在线/离线 | 电量 | 信号 | 设备（竖线分隔；离线只留 离线 | 设备）
  const status = document.createElement('div')
  status.className = `cockpit-detail-card__status ${
    d.online ? 'is-online' : 'is-offline'
  }`
  const addSep = () => {
    const sep = document.createElement('span')
    sep.className = 'cockpit-detail-card__sep'
    status.appendChild(sep)
  }
  const addSeg = (icon: SVGElement | null, text: string, extra?: string) => {
    const seg = document.createElement('span')
    seg.className = `cockpit-detail-card__seg${extra ? ' ' + extra : ''}`
    if (icon) {
      const ic = document.createElement('span')
      ic.className = 'cockpit-detail-card__seg-icon'
      ic.appendChild(icon)
      seg.appendChild(ic)
    }
    const t = document.createElement('span')
    t.textContent = text
    seg.appendChild(t)
    status.appendChild(seg)
  }
  // 状态点 + 在线/离线
  const stateSeg = document.createElement('span')
  stateSeg.className = 'cockpit-detail-card__seg cockpit-detail-card__state'
  const dot = document.createElement('span')
  dot.className = 'cockpit-detail-card__dot'
  const stateTxt = document.createElement('span')
  stateTxt.textContent = d.online ? '在线' : '离线'
  stateSeg.appendChild(dot)
  stateSeg.appendChild(stateTxt)
  status.appendChild(stateSeg)
  if (d.online) {
    addSep()
    addSeg(batteryIcon(d.battery), `${d.battery}%`)
    addSep()
    addSeg(signalIcon(d.signal), signalLabel(d.signal))
  }
  addSep()
  addSeg(null, `设备 ${d.deviceNo || '--'}`, 'cockpit-detail-card__seg--device')

  // 地址行：pin + 地址
  const addr = document.createElement('div')
  addr.className = 'cockpit-detail-card__addr'
  const pin = document.createElement('span')
  pin.className = 'cockpit-detail-card__pin'
  pin.appendChild(pinIcon())
  const addrTxt = document.createElement('span')
  addrTxt.className = 'cockpit-detail-card__addr-text'
  addrTxt.textContent = d.address || '位置解析中…'
  addr.appendChild(pin)
  addr.appendChild(addrTxt)

  // 地址下方小字
  const sub = document.createElement('div')
  sub.className = 'cockpit-detail-card__substate'
  sub.textContent = stateLabel

  // 4 数据格：已活动 / 总里程 / 停留点 / 拍照
  const statsBox = document.createElement('div')
  statsBox.className = 'cockpit-detail-card__stats'
  const cells = [
    {
      val: stats ? (stats.totalDurationSec / 3600).toFixed(1) : '--',
      unit: '小时',
      label: '已活动'
    },
    {
      val: stats ? (stats.totalDistanceMeters / 1000).toFixed(1) : '--',
      unit: 'km',
      label: '总里程'
    },
    {
      val: stats
        ? String(stats.stayPoints ? stats.stayPoints.length : 0)
        : '--',
      unit: '个',
      label: '停留点'
    },
    { val: stats ? String(stats.photoCount) : '--', unit: '张', label: '拍照' }
  ]
  cells.forEach((c) => {
    const cell = document.createElement('div')
    cell.className = 'cockpit-detail-card__cell'
    const top = document.createElement('div')
    top.className = 'cockpit-detail-card__cell-top'
    const val = document.createElement('span')
    val.className = 'cockpit-detail-card__cell-val'
    val.textContent = c.val
    const unit = document.createElement('span')
    unit.className = 'cockpit-detail-card__cell-unit'
    unit.textContent = c.unit
    top.appendChild(val)
    top.appendChild(unit)
    const label = document.createElement('div')
    label.className = 'cockpit-detail-card__cell-label'
    label.textContent = c.label
    cell.appendChild(top)
    cell.appendChild(label)
    statsBox.appendChild(cell)
  })

  // 查看完整轨迹
  const btn = document.createElement('button')
  btn.className = 'cockpit-detail-card__btn'
  btn.setAttribute('data-action', 'view-track')
  btn.textContent = '查看完整轨迹'

  card.appendChild(header)
  card.appendChild(status)
  card.appendChild(addr)
  card.appendChild(sub)
  card.appendChild(statsBox)
  card.appendChild(btn)
  return card
}

const CONTAINER_ID = 'cockpit-map-container'

const CockpitMap = forwardRef<CockpitMapRef, CockpitMapProps>(
  (
    {
      members,
      selectedUserID,
      trajectories,
      detail,
      onSelectMember,
      onViewTrack,
      onCloseDetail,
      fitKey,
      pois,
      detailStats,
      detailStateLabel,
      mapStyle
    },
    ref
  ) => {
    const mapRef = useRef<any>(null)
    const markersRef = useRef<any[]>([])
    const polylinesRef = useRef<any[]>([])
    const poiMarkersRef = useRef<any[]>([])
    // 卫星图层（切到卫星样式时叠加）
    const satelliteRef = useRef<any>(null)
    // 详情卡 InfoWindow
    const infoWindowRef = useRef<any>(null)
    // 用 ref 持有最新回调，避免 marker / 详情卡闭包过期
    const onSelectRef = useRef(onSelectMember)
    const onViewTrackRef = useRef(onViewTrack)
    const onCloseDetailRef = useRef(onCloseDetail)
    // marker 点击与地图空白点击的竞争守卫（点 marker 时跳过紧随的地图 click）
    const skipMapClickRef = useRef(false)
    // 待自适应标记：初始 / fitKey（切日期）变化时挂起，等新数据绘制后整体居中一次；
    // 之后轮询/选中不再打断当前视野
    const fitPendingRef = useRef(true)
    const mapReadyRef = useRef(false)
    // 地图就绪用 state 驱动重绘：数据可能先于地图 ready 到达，ready 后须用最新数据补绘一次
    const [mapReady, setMapReady] = useState(false)

    onSelectRef.current = onSelectMember
    onViewTrackRef.current = onViewTrack
    onCloseDetailRef.current = onCloseDetail

    /** 清除地图上所有成员 marker */
    const clearMarkers = useCallback(() => {
      if (markersRef.current.length > 0 && mapRef.current) {
        mapRef.current.remove(markersRef.current)
      }
      markersRef.current = []
    }, [])

    /** 用最新成员数据重绘 marker */
    const renderMarkers = useCallback(() => {
      if (!mapRef.current || !window.AMap) return
      clearMarkers()

      members.forEach((member) => {
        const ll = parseLngLat(member.location)
        if (!ll) return
        const selected = selectedUserID === member.userId
        const marker = new window.AMap.Marker({
          position: [ll.lng, ll.lat],
          content: buildMemberMarkerHtml(member, selected),
          // 底部阴影中心对准坐标点
          anchor: 'bottom-center',
          offset: new window.AMap.Pixel(0, 0),
          zIndex: selected ? 999 : 100
        })
        marker.on('click', () => {
          skipMapClickRef.current = true
          onSelectRef.current(member)
        })
        mapRef.current.add(marker)
        markersRef.current.push(marker)
      })
    }, [members, selectedUserID, clearMarkers])

    /** 清除地图上所有轨迹折线 */
    const clearPolylines = useCallback(() => {
      if (polylinesRef.current.length > 0 && mapRef.current) {
        mapRef.current.remove(polylinesRef.current)
      }
      polylinesRef.current = []
    }, [])

    /** 渲染各成员轨迹折线（实线段蓝色，中断段红色虚线） */
    const renderTrajectories = useCallback(() => {
      if (!mapRef.current || !window.AMap) return
      clearPolylines()

      // 有成员被选中时，其它成员轨迹淡化（0.6）以突出选中；无选中时统一 0.8
      const hasSelection = !!selectedUserID
      trajectories.forEach((trajectory, idx) => {
        const isSelected = trajectory.userId === selectedUserID
        // 各成员按调色板轮流取色；选中成员高亮绿、加粗、带方向箭头、压在最上层
        const color = isSelected
          ? SELECTED_TRACK_COLOR
          : TRACK_COLORS[idx % TRACK_COLORS.length]
        const weight = isSelected ? 8 : 4
        const opacity = isSelected ? 1 : hasSelection ? 0.4 : 0.8
        trajectory.segments.forEach((segment) => {
          if (segment.path.length < 2) return
          const isGap = segment.gap
          const polyline = new window.AMap.Polyline({
            path: segment.path,
            strokeColor: color,
            strokeWeight: weight,
            strokeOpacity: opacity,
            // 选中轨迹加 2px 白色外边框
            isOutline: isSelected,
            outlineColor: '#fff',
            borderWeight: 2,
            // 正常段实线；中断段（未获取轨迹）虚线
            strokeStyle: isGap ? 'dashed' : 'solid',
            strokeDasharray: [10, 8],
            // 正常段显示方向箭头（中断段虚线不加）
            showDir: !isGap,
            lineJoin: 'round',
            zIndex: isSelected ? 60 : 50
          })
          mapRef.current.add(polyline)
          polylinesRef.current.push(polyline)
        })

        // 轨迹终点：10×10 实心圆，颜色 = 该成员轨迹色
        const segs = trajectory.segments
        const lastSeg = segs.length ? segs[segs.length - 1] : null
        const endPt =
          lastSeg && lastSeg.path.length
            ? lastSeg.path[lastSeg.path.length - 1]
            : null
        if (endPt) {
          const endDot = new window.AMap.CircleMarker({
            center: endPt,
            radius: 5,
            fillColor: color,
            fillOpacity: opacity,
            // 1px 白色外描边
            strokeColor: '#fff',
            strokeWeight: 1,
            strokeOpacity: opacity,
            zIndex: isSelected ? 61 : 51
          })
          mapRef.current.add(endDot)
          polylinesRef.current.push(endDot)
        }
      })
    }, [trajectories, selectedUserID, clearPolylines])

    /** 清除地图上所有点位 marker */
    const clearPois = useCallback(() => {
      if (poiMarkersRef.current.length > 0 && mapRef.current) {
        mapRef.current.remove(poiMarkersRef.current)
      }
      poiMarkersRef.current = []
    }, [])

    /** 渲染已保存的点位（图标 + 名称），常驻在地图上 */
    const renderPois = useCallback(() => {
      if (!mapRef.current || !window.AMap) return
      clearPois()

      pois.forEach((poi) => {
        // 用 DOM 构建（图标 url / 名称为后端字段，避免 innerHTML 引入 XSS）
        const el = document.createElement('div')
        el.className = 'cockpit-poi'
        if (poi.iconUrl) {
          const img = document.createElement('img')
          img.className = 'cockpit-poi__icon'
          img.src = poi.iconUrl
          img.alt = ''
          el.appendChild(img)
        }
        const name = document.createElement('span')
        name.className = 'cockpit-poi__name'
        name.textContent = poi.name
        el.appendChild(name)

        const marker = new window.AMap.Marker({
          position: [poi.lng, poi.lat],
          // 高德 Marker 运行时支持 HTMLElement content，类型声明仅标 string
          content: el as unknown as string,
          // 底部倒三角尖对准坐标
          anchor: 'bottom-center',
          offset: new window.AMap.Pixel(0, 0),
          // 低于成员气泡（100+），高于轨迹线（50）
          zIndex: 60
        })
        // 点击点位：移到中心并放大到合适比例
        marker.on('click', () => {
          // 跳过紧随的地图空白 click（避免取消已选中的成员）
          skipMapClickRef.current = true
          if (mapRef.current) {
            mapRef.current.setZoomAndCenter(16, [poi.lng, poi.lat])
          }
        })
        mapRef.current.add(marker)
        poiMarkersRef.current.push(marker)
      })
    }, [pois, clearPois])

    // 初始化地图（仅一次）
    useEffect(() => {
      let destroyed = false

      AMapLoader.load({
        key: CONFIG.GAODE_KEY,
        version: '2.0',
        plugins: ['AMap.Scale', 'AMap.ToolBar']
      })
        .then((AMap) => {
          if (destroyed) return
          window.AMap = AMap

          const map = new AMap.Map(CONTAINER_ID, {
            zoom: 11,
            // 远山黛（whitesmoke）；如需「徽山翠黛」自定义样式，替换为其 amap://styles/<id>
            mapStyle: 'amap://styles/whitesmoke',
            viewMode: '2D',
            features: MAP_FEATURES
          })
          map.addControl(new AMap.Scale())
          map.addControl(
            // 缩放控件放到地图左侧（右侧被成员栏遮挡）；抬高避开底部时间轴
            new AMap.ToolBar({ position: { bottom: '90px', left: '24px' } })
          )
          // 点击地图空白处 → 取消选中（若紧跟在 marker 点击之后则跳过）
          map.on('click', () => {
            if (skipMapClickRef.current) {
              skipMapClickRef.current = false
              return
            }
            onSelectRef.current(null)
          })

          mapRef.current = map
          mapReadyRef.current = true
          // 触发重渲染，让下方 watch effect 用「当前最新」的 members / trajectories 补绘，
          // 避免数据先于地图 ready 到达时 .then 里拿到旧闭包（空数据）只画一次空图
          setMapReady(true)
        })
        .catch((error) => {
          console.error('驾驶舱地图初始化失败:', error)
        })

      return () => {
        destroyed = true
        clearPolylines()
        clearMarkers()
        clearPois()
        if (infoWindowRef.current) {
          infoWindowRef.current.close()
          infoWindowRef.current = null
        }
        if (mapRef.current) {
          mapRef.current.destroy()
          mapRef.current = null
        }
        mapReadyRef.current = false
      }
      // 仅挂载时初始化，数据更新由下方 effect 驱动
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // 成员数据 / 选中态变化、或地图刚就绪 → 用最新数据重绘 marker
    useEffect(() => {
      if (mapReady) {
        renderMarkers()
      }
    }, [renderMarkers, mapReady])

    // 轨迹数据变化、或地图刚就绪 → 用最新数据重绘折线
    useEffect(() => {
      if (mapReady) {
        renderTrajectories()
      }
    }, [renderTrajectories, mapReady])

    // 点位变化、或地图刚就绪 → 重绘点位 marker
    useEffect(() => {
      if (mapReady) {
        renderPois()
      }
    }, [renderPois, mapReady])

    // 地图样式切换（纯净 / 普通 / 卫星）
    useEffect(() => {
      if (!mapReady || !mapRef.current || !window.AMap) return
      applyMapStyle(mapRef.current, window.AMap, mapStyle, satelliteRef)
    }, [mapStyle, mapReady])

    // 切日期（fitKey 变化）→ 挂起一次自适应（不在切换瞬间用旧数据 fit）
    useEffect(() => {
      fitPendingRef.current = true
    }, [fitKey])

    // 新数据绘制后，把「轨迹线 + 成员气泡」整体居中一次（fitKey 不入依赖，避免吃到旧 overlay）
    useEffect(() => {
      if (!mapReady || !mapRef.current || !fitPendingRef.current) return
      const overlays = [...polylinesRef.current, ...markersRef.current]
      if (overlays.length === 0) return
      fitPendingRef.current = false
      mapRef.current.setFitView(overlays, false, [80, 80, 80, 80])
    }, [mapReady, members, trajectories])

    // 选中成员详情变化 → 在气泡上方弹/切/关详情卡（InfoWindow）。
    // 位置用该成员气泡坐标（members 已按日期取源：今日 A3 / 历史 B1），
    // 而非 A4 detail.location（永远是实时最新位置），否则切历史后卡会弹在旧地址上。
    useEffect(() => {
      if (!mapReady || !mapRef.current || !window.AMap) return

      const bubble = detail
        ? members.find((m) => m.userId === detail.userId)
        : null
      const ll = bubble
        ? parseLngLat(bubble.location)
        : detail
          ? parseLngLat(detail.location)
          : null
      if (!detail || !ll) {
        if (infoWindowRef.current) infoWindowRef.current.close()
        return
      }

      if (!infoWindowRef.current) {
        // 高德 InfoWindow 运行时接受配置项，类型声明未带构造参数
        infoWindowRef.current = new (window.AMap.InfoWindow as any)({
          isCustom: true,
          // 关闭自动平移：否则打开详情卡时会把地图平移到「卡片刚好可见」就停，
          // 打断点击成员时的居中动画，导致气泡一出现在屏幕内就不再移到中心
          autoMove: false,
          anchor: 'bottom-center',
          offset: new window.AMap.Pixel(0, -68)
        })
      }

      const card = buildDetailCard(detail, detailStats, detailStateLabel)
      card.addEventListener('click', (e) => {
        // 阻止冒泡到地图 click（否则点卡片会被当成点空白而关闭）
        e.stopPropagation()
        const target = (e.target as HTMLElement).closest('[data-action]')
        if (!target) return
        const action = target.getAttribute('data-action')
        if (action === 'close') {
          onCloseDetailRef.current()
        } else if (action === 'view-track') {
          onViewTrackRef.current(detail.userId)
        }
      })
      infoWindowRef.current.setContent(card)
      infoWindowRef.current.open(mapRef.current, [ll.lng, ll.lat])
    }, [detail, members, mapReady, detailStats, detailStateLabel])

    useImperativeHandle(
      ref,
      () => ({
        centerTo: (lng: number, lat: number, keepCloserZoom?: boolean) => {
          const m = mapRef.current
          if (!m) return
          // 点击默认缩放 16；keepCloserZoom 且当前缩放更大（视野更近）→ 只平移、不缩小
          if (keepCloserZoom && m.getZoom() > 16) {
            m.setCenter([lng, lat])
          } else {
            m.setZoomAndCenter(16, [lng, lat])
          }
        },
        fitViewToAll: () => {
          if (mapRef.current && markersRef.current.length > 0) {
            mapRef.current.setFitView(
              markersRef.current,
              false,
              [60, 60, 60, 60]
            )
          }
        },
        resize: () => {
          const m = mapRef.current
          if (!m) return
          // 优先用高德 resize；兜底用「重设当前中心」强制重排，均不改变 center/zoom
          if (typeof m.resize === 'function') m.resize()
          else if (m.getCenter && m.setCenter) m.setCenter(m.getCenter())
        }
      }),
      []
    )

    return (
      <div
        className={`cockpit-map${
          mapStyle === 'satellite' ? ' is-satellite' : ''
        }`}
      >
        <div id={CONTAINER_ID} className="cockpit-map__canvas" />
      </div>
    )
  }
)

CockpitMap.displayName = 'CockpitMap'

export default CockpitMap
